import { useEffect, useState } from 'react';
import { Download, Share, PlusSquare, Smartphone, MonitorSmartphone } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';

const isStandalone = () =>
  (typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) ||
  (typeof window !== 'undefined' && window.navigator.standalone === true);
const isIOS = () => typeof window !== 'undefined' && /iphone|ipad|ipod/i.test(window.navigator.userAgent) && !window.MSStream;

export const InstallAppButton = ({ variant = 'outline', size = 'default', className = '', label = 'Instalar app', iconOnly = false }) => {
  const [available, setAvailable] = useState(false);
  const [ios, setIos] = useState(false);
  const [iosOpen, setIosOpen] = useState(false);

  useEffect(() => {
    if (isStandalone()) return;
    if (window.__deferredInstallPrompt) setAvailable(true);
    if (isIOS()) { setIos(true); setAvailable(true); }
    const onReady = () => setAvailable(true);
    const onInstalled = () => setAvailable(false);
    window.addEventListener('pwa-installable', onReady);
    window.addEventListener('pwa-installed', onInstalled);
    return () => { window.removeEventListener('pwa-installable', onReady); window.removeEventListener('pwa-installed', onInstalled); };
  }, []);

  if (!available) return null;

  const click = async () => {
    const dp = window.__deferredInstallPrompt;
    if (dp) {
      dp.prompt();
      try { const { outcome } = await dp.userChoice; if (outcome === 'accepted') setAvailable(false); } catch (e) { /* noop */ }
      window.__deferredInstallPrompt = null;
    } else if (ios) {
      setIosOpen(true);
    }
  };

  return (
    <>
      <Button variant={variant} size={iconOnly ? 'icon' : size} className={className} onClick={click} data-testid="install-app-button" aria-label={label} title={label}>
        <Download className="h-4 w-4" />{!iconOnly && <span>{label}</span>}
      </Button>
      <Dialog open={iosOpen} onOpenChange={setIosOpen}>
        <DialogContent data-testid="install-ios-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Smartphone className="h-5 w-5 text-brand-orange" /> Instalar en iPhone / iPad</DialogTitle>
            <DialogDescription>Añade Bodegon53 a tu pantalla de inicio en dos pasos desde Safari.</DialogDescription>
          </DialogHeader>
          <ol className="space-y-3 text-sm">
            <li className="flex items-start gap-3"><span className="grid place-items-center h-7 w-7 shrink-0 rounded-full bg-brand-navy text-brand-cream text-xs font-bold">1</span><span className="flex items-center gap-1.5 pt-1">Toca el botón <strong>Compartir</strong> <Share className="h-4 w-4 inline text-brand-orange" /> en la barra de Safari.</span></li>
            <li className="flex items-start gap-3"><span className="grid place-items-center h-7 w-7 shrink-0 rounded-full bg-brand-navy text-brand-cream text-xs font-bold">2</span><span className="flex items-center gap-1.5 pt-1">Elige <strong>Añadir a pantalla de inicio</strong> <PlusSquare className="h-4 w-4 inline text-brand-orange" /> y confirma.</span></li>
          </ol>
          <p className="text-xs text-muted-foreground flex items-center gap-1.5"><MonitorSmartphone className="h-4 w-4" /> En Android, Windows y escritorio (Chrome/Edge) usa el botón «Instalar app».</p>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default InstallAppButton;
